import React, { useState, useRef, useCallback, useEffect } from 'react';
import firebase, { firestore, functions } from '../firebase/firebase';
import {
  useHistory,
} from 'react-router-dom';
import { Typography, Tabs, Tab, Grid, Box, Button, CircularProgress, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import ApplicationPersonalInfo from './ApplicationPersonalInfo';
import MemberAvatar from './MemberAvatar';
import queryLegacyMember from '../firebase/queryLegacyMember';
import { isArray } from '@material-ui/data-grid';
import { saveMember } from '../firebase/saveMember';
import validatePersonalId from '../utilities/validatePersonalId';
import validateLmaNumber from '../utilities/validateLmaNumber';
import validateBirthDate from '../utilities/validateBirthDate';
import AlertDialog from './AlertDialog';
import { deleteMember } from '../firebase/deleteMember';

const personnummer = require('personnummer.js');

function MemberOverview({ userId, user, setUser, localUser, setLocalUser, storeGroups, onClose }) {
  let history = useHistory();
  const isMounted = useRef(false);
  const [usersWithSamePersonalId, setUsersWithSamePersonalId] = useState([]);
  const [legacyMember, setLegacyMember] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const didDelete = useRef(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbar = useRef();
  const snackbars = {
    saveSuccess: {
      text: "Ändringar sparade",
      severity: 'success',
    },
    saveFailure: {
      text: "Kunde inte spara",
      severity: 'error',
    },
  };


  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Personal ID was changed.
  useEffect(() => {
    if (
      !personnummer.validate(localUser?.personalId) ||
      localUser?.hasNoPersonalId
    ) {
      setUsersWithSamePersonalId([]);
      setLegacyMember(null);
      return;
    }
    const queryDuplicates = async () => {
      let queryRef = firestore.collection('users')
        .where("personalId", "==", personnummer.normalise(localUser?.personalId));
      if (typeof localUser?.id === 'string' && localUser.id.length > 0) {
        queryRef = queryRef.where(firebase.firestore.FieldPath.documentId(), "!=", localUser?.id);
      }
      const otherUserDocs = [];
      try {
        const querySnapshot = await queryRef.get();
        querySnapshot.forEach((userDoc) => {
          otherUserDocs.push({
            id: userDoc.id,
            reset: false,
            ...userDoc.data(),
          });
        });
      } catch (error) {
        console.error("Could not check personal ID. ", error);
      }
      isMounted.current && setUsersWithSamePersonalId(otherUserDocs);
    };
    queryDuplicates();

    const handleQueryLegacyMember = async () => {
      const legacyMember = await queryLegacyMember(localUser?.personalId);
      if (legacyMember !== null) {
        isMounted.current && setLegacyMember(legacyMember);
      }
    };
    handleQueryLegacyMember();

  }, [localUser?.personalId]);

  // Legacy member was updated.
  useEffect(() => {
    if (legacyMember === null) { return; }
    const district = typeof legacyMember.district === 'string' ?
      legacyMember.district : null
    const category = typeof legacyMember.memberCategory !== 'undefined' &&
      isArray(legacyMember.memberCategory) ?
      legacyMember.memberCategory : [];
    setLocalUser({
      ...localUser,
      district,
      category,
    });
  }, [legacyMember]);

  const handleChange = (data) => {
    setLocalUser({
      ...localUser,
      ...data,
    });
  };

  const submissionStoreGroup = () => {
    const storeGroup = storeGroups?.find(storeGroup => storeGroup.ref.isEqual(localUser?.storeGroupRef ? localUser.storeGroupRef : null));
    return storeGroup;
  };

  const handleValidatePersonalId = () => {
    return validatePersonalId(localUser);
  };

  const handleValidateLmaNumber = () => {
    return validateLmaNumber(localUser?.lmaNumber, localUser?.birthDate);
  };

  const handleValidateBirthDate = () => {
    return validateBirthDate(localUser?.birthDate, localUser?.lmaNumber);
  };

  const handleValidateStoreGroup = () => {
    return typeof localUser?.storeGroupRef !== 'undefined' && localUser.storeGroupRef !== null;
  }

  const canSave = () => {
    let allowed = true;
    if (
      !handleValidatePersonalId() ||
      !handleValidateLmaNumber() ||
      !handleValidateBirthDate() ||
      !handleValidateStoreGroup()
    ) {
      allowed = false;
    };

    usersWithSamePersonalId.forEach((user) => {
      if (!user.reset) allowed = false;
    });
    return allowed;
  };

  const handleSaveMember = async () => {
    const result = await saveMember({
      userId: userId,
      user: { ...localUser, storeGroup: submissionStoreGroup() },
      setUser,
      usersWithSamePersonalId: usersWithSamePersonalId,
      setUsersWithSamePersonalId,
      legacyMember: legacyMember,
    });
    if (!isMounted.current) { return; }
    setIsSaving(false);
    snackbar.current = result.success ? snackbars.saveSuccess : snackbars.saveFailure;
    snackbar.current.text = result.message;
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!isSaving) { return; }
    handleSaveMember();
  }, [isSaving]);

  const handleSave = () => {
    setIsSaving(true);
  };

  const handleDeleteMember = async () => {
    const result = await deleteMember({
      userId: userId.current,
    });
    if (!isMounted.current) { return; }
    setIsDeleting(false);
    didDelete.current = true;
    snackbar.current = result.success ? snackbars.saveSuccess : snackbars.saveFailure;
    snackbar.current.text = result.message;
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!isDeleting) { return; }
    handleDeleteMember();
  }, [isDeleting]);

  const handleDelete = () => {
    setIsDeleting(true);
  }

  const handleSnackbarClose = (event, reason) => {
    setSnackbarOpen(false);
    if (didDelete.current) {
      history.push('/kunder/');
    }
  };

  return (
    <React.Fragment>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ marginLeft: 100, marginTop: 50 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.current?.severity}>
          {snackbar.current?.text}
        </Alert>
      </Snackbar>

      <Grid container spacing={2}>
        <Grid item xs={6}>
        {localUser?.physicalMemberCard && (
            <Typography
              component="h1"
              variant="h5"
              color="primary"
              style={{ textAlign: "left", marginBottom: 30 }}
            >
              Kund med fysiskt medlemskort
            </Typography>
          )}
          <ApplicationPersonalInfo
            type='membership-overview'
            submission={localUser}
            storeGroups={storeGroups}
            onChange={handleChange}
            usersWithSamePersonalId={usersWithSamePersonalId}
            setUsersWithSamePersonalId={setUsersWithSamePersonalId}
            approvalToggle={false}
          />
        </Grid>
        <Grid item xs={6}>
          {!localUser?.physicalMemberCard &&
            <MemberAvatar user={user} />
          }
          {
            localUser?.isNewMember === false &&
            <Button
              variant='outlined'
              color='secondary'
              style={{ marginTop: 40 }}
              disabled={isDeleting}
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Radera konto
            </Button>
          }
          {isDeleting &&
            <CircularProgress
              size={20}
              style={{ position: 'relative', left: 10, top: 25 }}
            />
          }
        </Grid>
        <Grid item xs={12}>
          <Box display='flex' justifyContent='left' alignItems='center'>
            <Button
              variant='outlined'
              color='primary'
              style={{
                marginRight: 20,
                width: 150,
              }}
              onClick={onClose}
            >
              Stäng
            </Button>
            <Button
              variant='contained'
              color='primary'
              style={{ width: 150 }}
              onClick={handleSave}
              disabled={!canSave() || isSaving}
              startIcon={<SaveIcon />}
            >
              Spara
            </Button>
            {isSaving &&
              <CircularProgress
                size={20}
                style={{ marginLeft: 10 }}
              />
            }
          </Box>
        </Grid>
        <Grid item xs={12}>

        </Grid>
        <Grid item xs={12}>

        </Grid>
      </Grid>

      <AlertDialog
        title="Radera konto?"
        description="Data kopplat till kontot kommer raderas, inklusive inlogg och profilbild."
        confirm={handleDelete}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        confirmButtonColor="secondary"
        confirmButtonTitle="Radera"
      />

    </React.Fragment>
  );

}

export default MemberOverview;